import React, { useState } from "react";
import { Button, Flex, Upload, Spin, message, Modal, Typography, Tooltip } from "antd";
import { DeleteOutlined, InboxOutlined } from "@ant-design/icons";
import styled, { createGlobalStyle } from "styled-components";
import { useCRF } from "./useCRF";
import { fileToBase64 } from "../../../services/uploadService";

const GlobalStyle = createGlobalStyle`
  * {
    font-family: 'Open Sans', sans-serif !important;
    .ant-upload-drag-icon svg {
      fill: #426BBA;
    }
    .ant-collapse-expand-icon {
      display: none !important;
    }
    .ant-collapse-header-text {
      color: #426BBA;
      font-weight: 600;
    }
  }
`;

const StyledButton = styled(Button)`
  background: #e3ecff;
  color: #426bba !important;
  font-weight: 600;
  font-size: 14px;
  border: none;
  border-radius: 1rem;
  box-shadow: 0 2px 6px rgba(66, 107, 186, 0.2);
  padding: 4px 16px;

  &:hover {
    background: #d7e4ff !important;
    color: #426bba !important;
  }
`;

const { Dragger } = Upload;
const { Title } = Typography;

const UploadDoc = () => {
  const {
    setFileList,
    fileList,
    handleBase64Upload,
    loading,
    setLoading,
    setTip,
    protocolId,
    setProtocolId,
    setIsFileDigitized,
    isFileDigitized,
    fetchProtocolVersions
  } = useCRF();

  const [activeKeys, setActiveKeys] = useState(["1"]);

  // A protocol can only have one file: once one is selected, disable the upload
  // area (with a hover hint) so the user deletes it first — cleaner than the
  // previous "file already exists" popup.
  const hasFile = fileList && fileList.length > 0;

  const handleUpload = async () => {
    if (!fileList || fileList.length === 0) {
      message.warning("Please select a file to upload.");
      return;
    }
    setLoading(true);
    setTip("File is being uploaded...");
    const file = fileList[0];
    const base64File = await fileToBase64(file);
    handleBase64Upload(base64File);
  };

  const props = {
    multiple: false,
    maxCount: 1, // allow strictly one file
    disabled: hasFile, // block the picker entirely while a file is already selected
    beforeUpload: (file) => {
      // Restrict file types (only PDF & DOCX)
      const allowedTypes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      const allowedExtensions = [".pdf", ".docx"];

      const fileExt = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();

      const isAllowedType = allowedTypes.includes(file.type) || allowedExtensions.includes(fileExt);

      if (!isAllowedType) {
        Modal.error({
          title: "Invalid File Type",
          content: "Only PDF and DOCX files are allowed.",
        });
        return Upload.LIST_IGNORE; // reject
      }

      if (fileList.length > 0) {
        Modal.warning({
          title: "File already exists",
          content: "You must delete the existing file before uploading a new one.",
        });
        return Upload.LIST_IGNORE; // block upload
      }

      setProtocolId(null);
      setIsFileDigitized(false);
      setFileList([file]); // add new file
      return false; // prevent auto upload
    },
    fileList,
    onRemove: (file) => {
      // use same delete handler with confirmation
      handleDelete(file);
      return false; // prevent default removal
    },
    accept: ".pdf,.docx",
  };

  // delete with confirmation
  const handleDelete = (file) => {
    Modal.confirm({
      title: "Delete File",
      content: `Are you sure you want to delete "${file.name}"?`,
      okText: "Yes",
      cancelText: "No",
      onOk: () => {
        setFileList((prev) => prev.filter((item) => item.uid !== file.uid));
        setIsFileDigitized(false);
        setProtocolId(null);
        fetchProtocolVersions()
      },
    });
  };

  return (
    <div style={{ padding: "5px" }}>
      <GlobalStyle />
      <Spin spinning={false} tip="Loading, please wait...">
        <Spin spinning={false} tip="Uploading...">
          <Title level={5} style={{ color: "#426bba", lineHeight: 1.4, margin: "0.25rem" }}>
            <span style={{ fontSize: "14px", fontWeight: 600 }}>For 'CRF' Deliverable,</span> <br />
            <span style={{ fontSize: "16px", fontWeight: 800 }}>Choose a Protocol:</span>
          </Title>

          <Tooltip title={hasFile ? "Please delete the existing file before uploading a new one." : ""}>
            {/* wrapper div so the tooltip still triggers on hover while the dragger is disabled */}
            <div>
              <Dragger showUploadList={false} {...props}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined style={{ color: "#27a6a4ff" }} />
                </p>
                <p className="ant-upload-text">Click or Drag file here to upload</p>
                <p className="ant-upload-hint">Supports PDF/DOCX files</p>
              </Dragger>
            </div>
          </Tooltip>
          {fileList && fileList?.length !== 0 && (
            <div>
              <h6 style={{ fontSize: 11, margin: "5px 0px 0px 5px" }}> Selected File : </h6>
              {fileList.map((file) => (
                <span
                  key={file.uid}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    margin: "0px 10px 10px 10px",
                  }}
                >
                  <span style={{ flex: 1 }}>{file.name}</span>
                  <DeleteOutlined
                    style={{
                      color: "red",
                      cursor: "pointer",
                    }}
                    onClick={() => handleDelete(file)}
                  />
                </span>
              ))}
            </div>
          )}
          <Flex justify="center" style={{ margin: "20px 0px 4px 0px" }}>
            {!isFileDigitized && (
              <StyledButton type="primary" onClick={handleUpload}>
                Upload
              </StyledButton>
            )}
          </Flex>
        </Spin>
      </Spin>
    </div>
  );
};

export default UploadDoc;
